import { useId, useMemo, useState, type JSX } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Button } from './button';
import { Badge } from './badge';
import { Icon } from './icon';
import { Popover, PopoverContent, PopoverTrigger } from './popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from './command';
import { catalogService } from '@/data/services/catalogService';
import { queryKeys } from '@/lib/query-keys';
import { normalizeFieldText } from '@/data/fieldPatterns';
import type { FieldValue } from '@/data/schemas/fieldPatterns.schemas';
import { slugifyItalian } from '@/data/idGenerator';
import { cn } from '@/lib/utils';
import { newId } from '@/utils/ids';
import { inputVariants } from './input';
import { useIsSafeMode } from '@/lib/safeMode';

type CatalogOption = {
  id: string;
  label: string;
  description?: string;
  isActive?: boolean;
};

const buildOptionId = (label: string, existingIds: Set<string>, fallbackPrefix: string): string => {
  const base = slugifyItalian(label);
  if (!base) {
    return `${fallbackPrefix}-${newId().slice(0, 8)}`;
  }
  if (!existingIds.has(base)) return base;
  let counter = 2;
  let candidate = `${base}-${counter}`;
  while (existingIds.has(candidate)) {
    counter += 1;
    candidate = `${base}-${counter}`;
  }
  return candidate;
};

const useCatalogOptions = (catalogId: string) =>
  useQuery({
    queryKey: queryKeys.catalogs.options(catalogId),
    queryFn: () => catalogService.getCatalogOptions(catalogId, { includeInactive: true }),
  });

const useCatalogOptionCreator = (catalogId: string) => {
  const queryClient = useQueryClient();

  const upsertOption = (option: CatalogOption) => {
    queryClient.setQueryData<CatalogOption[]>(queryKeys.catalogs.options(catalogId), (prev) => {
      if (!prev) return [option];
      if (prev.some((item) => item.id === option.id)) return prev;
      return [...prev, option];
    });
  };

  const ensureOption = async (label: string, optionId?: string): Promise<CatalogOption> => {
    const created = await catalogService.ensureCatalogOption(catalogId, {
      label,
      optionId,
    });
    upsertOption(created);
    return created;
  };

  return { ensureOption };
};

export function CatalogSuggestSelect({
  catalogId,
  value,
  onValueChange,
  placeholder = 'Seleziona o inserisci un valore',
  disabled = false,
  required = false,
  id,
  createIdPrefix = 'catalog',
}: {
  catalogId: string;
  value?: FieldValue;
  onValueChange?: (value?: FieldValue) => void;
  placeholder?: string;
  disabled?: boolean;
  required?: boolean;
  id?: string;
  createIdPrefix?: string;
}): JSX.Element {
  const isSafeMode = useIsSafeMode();
  const { data: options = [], isLoading } = useCatalogOptions(catalogId);
  const { ensureOption } = useCatalogOptionCreator(catalogId);
  const [open, setOpen] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [safeModeCustom, setSafeModeCustom] = useState('');
  const listId = useId();

  const activeOptions = useMemo(
    () => options.filter((option) => option.isActive !== false),
    [options]
  );

  const resolvedText = useMemo(() => {
    if (!value) return '';
    if (value.kind === 'option') {
      return options.find((option) => option.id === value.optionId)?.label ?? value.optionId;
    }
    if (value.kind === 'custom' || value.kind === 'text') {
      return normalizeFieldText(value.text);
    }
    return '';
  }, [options, value]);

  const normalizedInput = normalizeFieldText(inputValue);
  const hasCustomValue = normalizedInput.length > 0;
  const matchesOption = activeOptions.some(
    (option) => option.label.toLowerCase() === normalizedInput.toLowerCase()
  );

  const handleSelectOption = (optionId: string) => {
    onValueChange?.({ kind: 'option', optionId });
    setOpen(false);
  };

  const handleUseCustom = async () => {
    if (!hasCustomValue) return;
    const existing = activeOptions.find(
      (option) => option.label.toLowerCase() === normalizedInput.toLowerCase()
    );
    if (existing) {
      onValueChange?.({ kind: 'option', optionId: existing.id });
      setOpen(false);
      return;
    }

    try {
      const existingIds = new Set(activeOptions.map((option) => option.id));
      const optionId = buildOptionId(normalizedInput, existingIds, createIdPrefix);
      const created = await ensureOption(normalizedInput, optionId);
      onValueChange?.({ kind: 'option', optionId: created.id });
    } catch {
      onValueChange?.({ kind: 'custom', text: normalizedInput });
    }
    setOpen(false);
  };

  const handleClear = () => {
    onValueChange?.(undefined);
    setInputValue('');
    setOpen(false);
  };

  // Consolidate state updates in onOpenChange to prevent race conditions
  // between click handler and popover state on slower systems
  const handleOpenChange = (isOpen: boolean) => {
    if (isOpen && !disabled) {
      setInputValue(resolvedText);
    }
    setOpen(isOpen);
  };

  // SAFE MODE: Use native select + text input for better compatibility
  if (isSafeMode) {
    const unsetValue = '__unset__';
    const customValue = '__custom__';

    const resolvedSelectValue =
      value?.kind === 'option'
        ? value.optionId
        : value?.kind === 'custom' || value?.kind === 'text'
          ? customValue
          : !required
            ? unsetValue
            : '';

    const handleSafeModeChange = (selectValue: string) => {
      if (selectValue === unsetValue) {
        onValueChange?.(undefined);
        setSafeModeCustom('');
      } else if (selectValue === customValue) {
        // Keep current custom value or clear
        if (!safeModeCustom) {
          setSafeModeCustom(resolvedText);
        }
      } else {
        onValueChange?.({ kind: 'option', optionId: selectValue });
        setSafeModeCustom('');
      }
    };

    const handleCustomBlur = async () => {
      const trimmed = normalizeFieldText(safeModeCustom);
      if (!trimmed) {
        if (!required) onValueChange?.(undefined);
        return;
      }
      // Try to find matching option first
      const existing = activeOptions.find(
        (opt) => opt.label.toLowerCase() === trimmed.toLowerCase()
      );
      if (existing) {
        onValueChange?.({ kind: 'option', optionId: existing.id });
        return;
      }
      // Create new option
      try {
        const existingIds = new Set(activeOptions.map((opt) => opt.id));
        const optionId = buildOptionId(trimmed, existingIds, createIdPrefix);
        const created = await ensureOption(trimmed, optionId);
        onValueChange?.({ kind: 'option', optionId: created.id });
      } catch {
        onValueChange?.({ kind: 'custom', text: trimmed });
      }
    };

    const showCustomInput =
      resolvedSelectValue === customValue || value?.kind === 'custom' || value?.kind === 'text';

    return (
      <div className="space-y-2">
        <select
          id={id}
          value={resolvedSelectValue}
          onChange={(e) => handleSafeModeChange(e.target.value)}
          disabled={disabled || isLoading}
          required={required}
          className={cn(inputVariants(), 'pr-8')}
        >
          {!required && <option value={unsetValue}>Non specificato</option>}
          {isLoading && <option value="">Caricamento...</option>}
          {activeOptions.map((option) => (
            <option key={option.id} value={option.id}>
              {option.label}
            </option>
          ))}
          <option value={customValue}>+ Aggiungi valore personalizzato</option>
        </select>
        {showCustomInput && (
          <input
            type="text"
            value={safeModeCustom || resolvedText}
            onChange={(e) => setSafeModeCustom(e.target.value)}
            onBlur={handleCustomBlur}
            placeholder="Inserisci valore personalizzato"
            className={inputVariants()}
            disabled={disabled}
          />
        )}
      </div>
    );
  }

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild disabled={disabled || isLoading}>
        <button
          id={id}
          type="button"
          role="combobox"
          aria-controls={listId}
          aria-expanded={open}
          disabled={disabled || isLoading}
          className={cn(inputVariants({ size: 'md' }), 'justify-between text-left font-normal')}
        >
          <span
            className={cn(
              'truncate text-sm font-normal',
              resolvedText ? 'text-text-primary' : 'text-text-tertiary'
            )}
          >
            {resolvedText || (isLoading ? 'Caricamento...' : placeholder)}
          </span>
          <Icon name="expand_more" className="text-base text-text-tertiary" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
        <Command>
          <CommandInput
            value={inputValue}
            onValueChange={setInputValue}
            placeholder={isLoading ? 'Caricamento...' : 'Cerca o scrivi'}
          />
          <CommandList id={listId}>
            <CommandEmpty>Nessuna opzione trovata</CommandEmpty>
            {!required && (
              <CommandGroup>
                <CommandItem onSelect={handleClear}>Non specificato</CommandItem>
              </CommandGroup>
            )}
            {hasCustomValue && !matchesOption && (
              <CommandGroup>
                <CommandItem onSelect={handleUseCustom}>
                  Aggiungi &quot;{normalizedInput}&quot;
                </CommandItem>
              </CommandGroup>
            )}
            <CommandGroup heading="Suggerimenti">
              {activeOptions.map((option) => (
                <CommandItem key={option.id} onSelect={() => handleSelectOption(option.id)}>
                  {option.label}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

export function CatalogSuggestTextSelect({
  catalogId,
  value,
  onValueChange,
  placeholder = 'Seleziona o inserisci un valore',
  disabled = false,
  id,
  createIdPrefix = 'catalog',
}: {
  catalogId: string;
  value?: string;
  onValueChange?: (value?: string) => void;
  placeholder?: string;
  disabled?: boolean;
  id?: string;
  createIdPrefix?: string;
}): JSX.Element {
  const { data: options = [], isLoading } = useCatalogOptions(catalogId);
  const { ensureOption } = useCatalogOptionCreator(catalogId);
  const [open, setOpen] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const listId = useId();

  const activeOptions = useMemo(
    () => options.filter((option) => option.isActive !== false),
    [options]
  );
  const normalizedValue = value ? normalizeFieldText(value) : '';
  const normalizedInput = normalizeFieldText(inputValue);
  const matchesInputOption = activeOptions.some(
    (option) => option.label.toLowerCase() === normalizedInput.toLowerCase()
  );

  const handleSelectOption = (optionLabel: string) => {
    onValueChange?.(optionLabel);
    setOpen(false);
  };

  const handleUseCustom = async () => {
    if (!normalizedInput) return;
    const existing = activeOptions.find(
      (option) => option.label.toLowerCase() === normalizedInput.toLowerCase()
    );
    if (existing) {
      onValueChange?.(existing.label);
      setOpen(false);
      return;
    }

    try {
      const existingIds = new Set(activeOptions.map((option) => option.id));
      const optionId = buildOptionId(normalizedInput, existingIds, createIdPrefix);
      const created = await ensureOption(normalizedInput, optionId);
      onValueChange?.(created.label);
    } catch {
      onValueChange?.(normalizedInput);
    }
    setOpen(false);
  };

  // Consolidate state updates in onOpenChange to prevent race conditions
  // between click handler and popover state on slower systems
  const handleOpenChange = (isOpen: boolean) => {
    if (isOpen && !disabled) {
      setInputValue(normalizedValue);
    }
    setOpen(isOpen);
  };

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild disabled={disabled || isLoading}>
        <button
          id={id}
          type="button"
          role="combobox"
          aria-controls={listId}
          aria-expanded={open}
          disabled={disabled || isLoading}
          className={cn(inputVariants({ size: 'md' }), 'justify-between text-left font-normal')}
        >
          <span
            className={cn(
              'truncate text-sm font-normal',
              normalizedValue ? 'text-text-primary' : 'text-text-tertiary'
            )}
          >
            {normalizedValue || (isLoading ? 'Caricamento...' : placeholder)}
          </span>
          <Icon name="expand_more" className="text-base text-text-tertiary" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
        <Command>
          <CommandInput
            value={inputValue}
            onValueChange={setInputValue}
            placeholder={isLoading ? 'Caricamento...' : 'Cerca o scrivi'}
          />
          <CommandList id={listId}>
            <CommandEmpty>Nessuna opzione trovata</CommandEmpty>
            {normalizedInput.length > 0 && !matchesInputOption && (
              <CommandGroup>
                <CommandItem onSelect={handleUseCustom}>
                  Aggiungi &quot;{normalizedInput}&quot;
                </CommandItem>
              </CommandGroup>
            )}
            <CommandGroup heading="Suggerimenti">
              {activeOptions.map((option) => (
                <CommandItem key={option.id} onSelect={() => handleSelectOption(option.label)}>
                  {option.label}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

export function CatalogSuggestMultiSelect({
  catalogId,
  value,
  onValueChange,
  placeholder = 'Aggiungi azioni',
  disabled = false,
  createIdPrefix = 'azione',
  className,
}: {
  catalogId: string;
  value: string[];
  onValueChange?: (value: string[]) => void;
  placeholder?: string;
  disabled?: boolean;
  createIdPrefix?: string;
  className?: string;
}): JSX.Element {
  const { data: options = [], isLoading } = useCatalogOptions(catalogId);
  const { ensureOption } = useCatalogOptionCreator(catalogId);
  const [open, setOpen] = useState(false);
  const [inputValue, setInputValue] = useState('');

  const activeOptions = useMemo(
    () => options.filter((option) => option.isActive !== false),
    [options]
  );

  const selectedLabels = useMemo(
    () => value.map((id) => options.find((option) => option.id === id)?.label ?? id),
    [options, value]
  );

  const matchesOption = activeOptions.some(
    (option) => option.label.toLowerCase() === normalizeFieldText(inputValue).toLowerCase()
  );

  const handleToggle = (optionId: string) => {
    if (disabled) return;
    const next = value.includes(optionId)
      ? value.filter((item) => item !== optionId)
      : [...value, optionId];
    onValueChange?.(next);
  };

  const handleUseCustom = async () => {
    if (disabled) return;
    const normalized = normalizeFieldText(inputValue);
    if (!normalized) return;
    const existing = activeOptions.find(
      (option) => option.label.toLowerCase() === normalized.toLowerCase()
    );
    if (existing) {
      if (!value.includes(existing.id)) {
        onValueChange?.([...value, existing.id]);
      }
      setOpen(false);
      return;
    }

    try {
      const existingIds = new Set(activeOptions.map((option) => option.id));
      const optionId = buildOptionId(normalized, existingIds, createIdPrefix);
      const created = await ensureOption(normalized, optionId);
      onValueChange?.([...value, created.id]);
    } catch {
      onValueChange?.([...value, normalized]);
    }
    setOpen(false);
  };

  return (
    <div
      className={cn(
        'flex min-h-10 flex-wrap items-center gap-2 rounded-md border border-[var(--input-border-default)] bg-surface px-3 py-2 text-sm transition-all duration-150',
        className
      )}
    >
      {selectedLabels.length > 0 ? (
        selectedLabels.map((label, index) => (
          <Badge key={`${label}-${index}`} variant="secondary" className="flex items-center gap-1">
            {label}
            <button
              type="button"
              onClick={() => handleToggle(value[index])}
              disabled={disabled}
              className="text-text-tertiary transition-colors hover:text-text-primary"
              aria-label={`Rimuovi ${label}`}
            >
              <Icon name="close" className="text-sm" />
            </button>
          </Badge>
        ))
      ) : (
        <span className="text-text-tertiary">{placeholder}</span>
      )}
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild disabled={disabled || isLoading}>
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={disabled || isLoading}
            className="h-7 px-2"
          >
            {isLoading ? '...' : 'Aggiungi'}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
          <Command>
            <CommandInput
              value={inputValue}
              onValueChange={setInputValue}
              placeholder={isLoading ? 'Caricamento...' : 'Cerca o scrivi'}
            />
            <CommandList>
              <CommandEmpty>Nessuna opzione trovata</CommandEmpty>
              {normalizeFieldText(inputValue).length > 0 && !matchesOption && (
                <CommandGroup>
                  <CommandItem onSelect={handleUseCustom}>
                    Aggiungi &quot;{normalizeFieldText(inputValue)}&quot;
                  </CommandItem>
                </CommandGroup>
              )}
              <CommandGroup heading="Suggerimenti">
                {activeOptions.map((option) => (
                  <CommandItem
                    key={option.id}
                    onSelect={() => handleToggle(option.id)}
                    disabled={disabled}
                  >
                    <span className="flex flex-1 items-center gap-2">
                      {value.includes(option.id) ? (
                        <Icon name="check" className="text-sm text-brand-blue-600" />
                      ) : (
                        <span className="w-4" />
                      )}
                      {option.label}
                    </span>
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  );
}

export function CatalogSuggestTagsInput({
  catalogId,
  value,
  onValueChange,
  placeholder = 'Aggiungi valori',
  disabled = false,
  createIdPrefix = 'catalog',
  className,
}: {
  catalogId: string;
  value: string[];
  onValueChange?: (value: string[]) => void;
  placeholder?: string;
  disabled?: boolean;
  createIdPrefix?: string;
  className?: string;
}): JSX.Element {
  const { data: options = [], isLoading } = useCatalogOptions(catalogId);
  const { ensureOption } = useCatalogOptionCreator(catalogId);
  const [open, setOpen] = useState(false);
  const [inputValue, setInputValue] = useState('');

  const activeOptions = useMemo(
    () => options.filter((option) => option.isActive !== false),
    [options]
  );

  const normalizedValues = useMemo(
    () => value.map((item) => normalizeFieldText(item)).filter((item) => item.length > 0),
    [value]
  );

  const valueSet = useMemo(
    () => new Set(normalizedValues.map((item) => item.toLowerCase())),
    [normalizedValues]
  );

  const handleAddValue = async (label: string) => {
    if (disabled) return;
    const normalized = normalizeFieldText(label);
    if (!normalized) return;
    if (valueSet.has(normalized.toLowerCase())) return;
    onValueChange?.([...normalizedValues, normalized]);
  };

  const handleUseCustom = async () => {
    if (disabled) return;
    const normalized = normalizeFieldText(inputValue);
    if (!normalized) return;
    const existing = activeOptions.find(
      (option) => option.label.toLowerCase() === normalized.toLowerCase()
    );
    if (existing) {
      await handleAddValue(existing.label);
      setOpen(false);
      return;
    }

    try {
      const existingIds = new Set(activeOptions.map((option) => option.id));
      const optionId = buildOptionId(normalized, existingIds, createIdPrefix);
      const created = await ensureOption(normalized, optionId);
      await handleAddValue(created.label);
    } catch {
      await handleAddValue(normalized);
    }
    setOpen(false);
  };

  return (
    <div
      className={cn(
        'flex min-h-10 flex-wrap items-center gap-2 rounded-md border border-[var(--input-border-default)] bg-surface px-3 py-2 text-sm transition-all duration-150',
        className
      )}
    >
      {normalizedValues.length > 0 ? (
        normalizedValues.map((label) => (
          <Badge key={label} variant="secondary" className="flex items-center gap-1">
            {label}
            <button
              type="button"
              onClick={() => onValueChange?.(normalizedValues.filter((item) => item !== label))}
              disabled={disabled}
              className="text-text-tertiary transition-colors hover:text-text-primary"
              aria-label={`Rimuovi ${label}`}
            >
              <Icon name="close" className="text-sm" />
            </button>
          </Badge>
        ))
      ) : (
        <span className="text-text-tertiary">{placeholder}</span>
      )}
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild disabled={disabled || isLoading}>
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={disabled || isLoading}
            className="h-7 px-2"
          >
            {isLoading ? '...' : 'Aggiungi'}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
          <Command>
            <CommandInput
              value={inputValue}
              onValueChange={setInputValue}
              placeholder={isLoading ? 'Caricamento...' : 'Cerca o scrivi'}
            />
            <CommandList>
              <CommandEmpty>Nessuna opzione trovata</CommandEmpty>
              {normalizeFieldText(inputValue).length > 0 && (
                <CommandGroup>
                  <CommandItem onSelect={handleUseCustom}>
                    Aggiungi &quot;{normalizeFieldText(inputValue)}&quot;
                  </CommandItem>
                </CommandGroup>
              )}
              <CommandGroup heading="Suggerimenti">
                {activeOptions.map((option) => (
                  <CommandItem
                    key={option.id}
                    onSelect={() => handleAddValue(option.label)}
                    disabled={disabled}
                  >
                    {option.label}
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  );
}
